"use client";

import { useState, useEffect } from "react";
import { X, Mail, Shield, Loader } from "lucide-react";
import "./Validation.css";

const ValidationModal = ({
  isOpen,
  onClose,
  onValidateCode,
  onValidateId,
  userEmail,
  isLoading = false,
  onResendCode,
}) => {
  const [validationMethod, setValidationMethod] = useState("code"); // 'code' or 'id'
  const [code, setCode] = useState("");
  const [uniqueId, setUniqueId] = useState("");
  const [errors, setErrors] = useState({});
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCode("");
      setUniqueId("");
      setErrors({});
      setValidationMethod("code");
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (validationMethod === "code") {
      if (!code.trim()) {
        setErrors({ code: "Verification code is required" });
        return;
      }
      // Backend expects: { code: "string", email: "string($email)" }
      onValidateCode({ code: code.trim(), email: userEmail });
    } else {
      if (!uniqueId.trim()) {
        setErrors({ uniqueId: "Unique ID is required" });
        return;
      }

      // Backend expects: { userdhmsid: "string", email: "user@example.com" }
      onValidateId({ userdhmsid: uniqueId.trim(), email: userEmail });
    }
  };

  const handleResendCode = async () => {
    if (!onResendCode) return;
    setIsResending(true);
    try {
      await onResendCode(userEmail);
    } finally {
      setIsResending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="validation-modal-overlay">
      <div className="validation-modal">
        <div className="validation-modal-header">
          <h2 className="validation-modal-title">Verify Your Identity</h2>
          <button
            className="validation-modal-close"
            onClick={onClose}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>
        <div className="validation-modal-content">
          <div className="validation-tabs">
            <button
              className={`validation-tab ${
                validationMethod === "code" ? "active" : ""
              }`}
              onClick={() => setValidationMethod("code")}
              disabled={isLoading}
            >
              <Mail size={16} />
              Email Code
            </button>
            <button
              className={`validation-tab ${
                validationMethod === "id" ? "active" : ""
              }`}
              onClick={() => setValidationMethod("id")}
              disabled={isLoading}
            >
              <Shield size={16} />
              Unique ID
            </button>
          </div>
          <form onSubmit={handleSubmit} className="validation-form">
            {validationMethod === "code" ? (
              <div className="validation-form-group">
                <label htmlFor="code">Verification Code</label>
                <p className="validation-description">
                  Enter the 4-digit code sent to {userEmail}
                </p>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  placeholder="0000"
                  className={`validation-input ${errors.code ? "error" : ""}`}
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={isLoading}
                />
                {errors.code && (
                  <span className="validation-error">{errors.code}</span>
                )}
                {onResendCode && (
                  <button
                    type="button"
                    className="validation-resend-btn"
                    onClick={handleResendCode}
                    disabled={isLoading || isResending}
                  >
                    {isResending ? "Sending..." : "Resend Code"}
                  </button>
                )}
              </div>
            ) : (
              <div className="validation-form-group">
                <label htmlFor="uniqueId">Unique ID</label>
                <p className="validation-description">
                  Enter your unique identification number
                </p>
                <input
                  type="text"
                  id="uniqueId"
                  value={uniqueId}
                  onChange={(e) => setUniqueId(e.target.value)}
                  placeholder="Enter your unique ID"
                  className={`validation-input ${
                    errors.uniqueId ? "error" : ""
                  }`}
                  disabled={isLoading}
                />
                {errors.uniqueId && (
                  <span className="validation-error">{errors.uniqueId}</span>
                )}
              </div>
            )}
            <button
              type="submit"
              className="validation-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader size={16} className="spinner" />
                  Verifying...
                </>
              ) : (
                "Verify Identity"
              )}
            </button>
          </form>
          <div className="validation-help">
            <p>
              {validationMethod === "code"
                ? "Didn't receive the code? Check your spam folder or try using your Unique ID instead."
                : "Don't have your Unique ID? Try using the email verification code instead."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationModal;
